// Audit filters live in the URL: shareable, Back-safe, and Reset is just
// "clear the query string" (every control is controlled by these values).

import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

export function useUrlFilters<K extends string>(keys: readonly K[]) {
  const [params, setParams] = useSearchParams()
  const values = Object.fromEntries(keys.map((k) => [k, params.get(k) ?? ''])) as Record<K, string>

  const set = useCallback(
    (patch: Partial<Record<K, string>>) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          for (const [k, v] of Object.entries(patch) as Array<[string, string | undefined]>) {
            if (v) next.set(k, v)
            else next.delete(k)
          }
          return next
        },
        { replace: true },
      ),
    [setParams],
  )

  const reset = useCallback(() => setParams(new URLSearchParams(), { replace: true }), [setParams])
  const active = keys.some((k) => !!values[k])
  return { values, set, reset, active }
}

/** A positive integer id from a URL value, else undefined (never sends junk to the server). */
export function idParam(v: string): number | undefined {
  const n = Number(v)
  return v && Number.isInteger(n) && n > 0 ? n : undefined
}

/** YYYY-MM-DD or undefined. */
export function dateParam(v: string): string | undefined {
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined
}
